// client/src/pages/School_Admin/Academics/TimetableExamsTab.js
//
// THE EXAM TIMETABLE, BUILT FROM THE WEEK EACH CLASS IS TAUGHT.
//
// An exam period is not a teaching week with the lessons swapped out. A class
// teaches Mathematics six times a week and sits it once; two classes that share
// a subject teach it at different hours and must sit it at the SAME hour, or the
// first class out of the hall carries the paper to the second.
//
// So the unit on this screen is the PAPER, not the class-subject: one row per
// subject, one sitting, and every class that takes it sits it then. What the
// grid below shows is the consequence — a class column can hold at most one
// paper per sitting, and if it cannot, the arithmetic says so before anybody
// prints anything.

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Alert, AlertTitle, Box, Button, Card, CardContent, Chip, Divider, IconButton,
  LinearProgress, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PublishIcon from '@mui/icons-material/Publish';
import RefreshIcon from '@mui/icons-material/Refresh';
import { fetchJson } from '../../../utils/fetchJson';
import { subjectChipColours } from '../../../utils/subjectColours.generated';
import { buildPdf, deliver } from './TimetableMasterGrid';

const API = '/api/academics/timetables';

const DEFAULT_SESSIONS = [
  { label: 'Morning', start: '08:00', end: '10:30' },
  { label: 'Afternoon', start: '13:00', end: '15:30' },
];

/* -------------------------------------------------------------------------- */

export default function ExamsTab({ readOnly }) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';

  const [days, setDays] = useState(5);
  const [sessions, setSessions] = useState(DEFAULT_SESSIONS);
  const [sequenceNumber, setSequenceNumber] = useState(1);
  const [name, setName] = useState('');

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [applied, setApplied] = useState(null);
  const [partial, setPartial] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const q = new URLSearchParams({ days: String(days), sessions: JSON.stringify(sessions) });
      setPlan(await fetchJson(`${API}/solver/exam-plan?${q}`));
    } catch (e) {
      setError(e?.message || 'Could not work out the exam timetable.');
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [days, sessions]);

  useEffect(() => { load(); }, [load]);

  const apply = async (acceptPartial) => {
    setApplying(true);
    setError('');
    setPartial(null);
    try {
      const body = await fetchJson(`${API}/solver/exam-apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          days, sessions, sequenceNumber, timetableName: name || null, acceptPartial: !!acceptPartial,
        }),
      });
      setApplied(body);
    } catch (e) {
      if (e?.data?.partial) setPartial(e.message);
      else setError(e?.message || 'Could not apply the exam timetable.');
    } finally {
      setApplying(false);
    }
  };

  /* -- the grid ------------------------------------------------------------ */

  const columns = useMemo(() => {
    const out = [];
    for (let d = 1; d <= days; d += 1) {
      sessions.forEach((s, i) => out.push({ day: d, session: i, ...s }));
    }
    return out;
  }, [days, sessions]);

  const cellOf = useMemo(() => {
    const m = new Map();
    for (const p of plan?.placements || []) m.set(`${p.classId}|${p.day}|${p.session}`, p);
    return m;
  }, [plan]);

  const classes = plan?.byClass || [];

  /* -- paper --------------------------------------------------------------- */

  const sheet = () => ({
    title: 'Exam timetable',
    subtitle: `${plan?.papers?.length || 0} papers · ${days} days · `
      + `${sessions.map((s) => `${s.label} ${s.start}`).join(' · ')}`,
    filename: `exam-timetable-sequence-${sequenceNumber}.pdf`,
    tables: [{
      head: ['Class', ...columns.map((c) => `Day ${c.day}\n${c.label}\n${c.start}–${c.end}`)],
      body: classes.map((cls) => [
        cls.name,
        ...columns.map((c) => {
          const p = cellOf.get(`${cls.id}|${c.day}|${c.session}`);
          return p ? p.subjectName : '';
        }),
      ]),
      breakColumns: new Set(),
    }],
  });

  const toPaper = (printIt) => {
    const s = sheet();
    deliver(buildPdf(s), s.filename, printIt);
  };

  /* ------------------------------------------------------------------------ */

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            How long the exams run
          </Typography>
          <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
            <TextField
              size="small" label="Days" type="number" sx={{ width: 100 }}
              inputProps={{ min: 1, max: 30 }}
              value={days}
              onChange={(e) => setDays(Math.min(30, Math.max(1, Number(e.target.value) || 1)))}
            />
            <Box>
              <Typography variant="caption" color="text.secondary">Sittings in a day</Typography>
              <Stack spacing={1} sx={{ mt: 0.5 }}>
                {sessions.map((s, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small" label="Name" sx={{ width: 130 }} value={s.label}
                      onChange={(e) => setSessions(sessions.map((x, j) => (j === i
                        ? { ...x, label: e.target.value } : x)))}
                    />
                    <TextField
                      size="small" label="From" type="time" sx={{ width: 120 }} value={s.start}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setSessions(sessions.map((x, j) => (j === i
                        ? { ...x, start: e.target.value } : x)))}
                    />
                    <TextField
                      size="small" label="To" type="time" sx={{ width: 120 }} value={s.end}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setSessions(sessions.map((x, j) => (j === i
                        ? { ...x, end: e.target.value } : x)))}
                    />
                    <IconButton
                      size="small" disabled={sessions.length <= 1}
                      onClick={() => setSessions(sessions.filter((_, j) => j !== i))}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  size="small" startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start' }}
                  onClick={() => setSessions([...sessions,
                    { label: `Sitting ${sessions.length + 1}`, start: '16:00', end: '18:00' }])}
                >
                  Another sitting
                </Button>
              </Stack>
            </Box>
            <Box sx={{ flex: 1 }} />
            <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
              Work it out again
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            A subject sits once, at the same hour for every class that takes it — so no class
            carries a paper out of the hall to another. The number of sittings therefore has to be
            at least the largest number of subjects any one class takes.
          </Typography>
          {loading && <LinearProgress sx={{ mt: 2 }} />}
        </CardContent>
      </Card>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      {applied && (
        <Alert severity="success" onClose={() => setApplied(null)}>
          <AlertTitle>Applied</AlertTitle>
          {applied.papers} papers written across {applied.classes} classes for sequence
          {' '}{applied.sequenceNumber}. They show on the timetable page under Exam.
        </Alert>
      )}

      {partial && (
        <Alert
          severity="warning"
          action={(
            <Button color="inherit" size="small" disabled={applying} onClick={() => apply(true)}>
              Apply anyway
            </Button>
          )}
        >
          <AlertTitle>Some papers have no sitting</AlertTitle>
          {partial}
        </Alert>
      )}

      {plan && (
        <>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              color={plan.ok ? 'success' : 'warning'}
              label={plan.ok ? 'Every paper has a sitting'
                : `${plan.unplaced.length} paper${plan.unplaced.length === 1 ? '' : 's'} with no sitting`}
            />
            <Chip size="small" variant="outlined" label={`${plan.papers.length} papers`} />
            <Chip size="small" variant="outlined" label={`${plan.sittings} sittings available`} />
            <Chip size="small" variant="outlined" label={`${classes.length} classes`} />
            <Box sx={{ flex: 1 }} />
            <TextField
              size="small" label="Sequence" type="number" sx={{ width: 110 }}
              inputProps={{ min: 1, max: 12 }}
              value={sequenceNumber}
              onChange={(e) => setSequenceNumber(Math.max(1, Number(e.target.value) || 1))}
            />
            <TextField
              size="small" label="Name (optional)" sx={{ width: 190 }}
              placeholder="First Sequence"
              value={name} onChange={(e) => setName(e.target.value)}
            />
            <Button size="small" startIcon={<PrintIcon />} onClick={() => toPaper(true)}>Print</Button>
            <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={() => toPaper(false)}>PDF</Button>
            <Button
              variant="contained" startIcon={<PublishIcon />}
              disabled={applying || readOnly || !plan.placements.length}
              onClick={() => apply(false)}
            >
              {applying ? 'Applying…' : 'Apply'}
            </Button>
          </Stack>

          {!!plan.suggestions.length && (
            <Alert severity="warning">
              <AlertTitle>What would make it fit</AlertTitle>
              <Stack spacing={0.5}>
                {plan.suggestions.map((s, i) => (
                  <Typography key={i} variant="body2">• {s.message}</Typography>
                ))}
              </Stack>
            </Alert>
          )}

          {!!plan.fromSetup.length && (
            <Alert severity="info">
              {plan.fromSetup.map((c) => c.name).join(', ')}
              {plan.fromSetup.length === 1 ? ' has' : ' have'} no class timetable in use, so
              their papers come from the subjects set up for them rather than from a week anybody
              is teaching. Generate and apply a class timetable first if you want these to match.
            </Alert>
          )}

          {/* THE GRID. Classes down, sittings across — the shape an exam
              timetable is pinned to a noticeboard in. */}
          <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 160 + columns.length * 120 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{
                    position: 'sticky', left: 0, zIndex: 2, minWidth: 160,
                    // NOT `palette.grey[N]`. This product's grey scale is a ladder
                    // built from the chosen palette: `grey[900]` is that palette's
                    // TEXT colour, which in the dark theme is near-white — so a
                    // header painted with it had near-white ink on a near-white
                    // ground and its labels vanished. `action.selected` is an
                    // overlay MUI derives from the mode and cannot invert.
                    bgcolor: theme.palette.action.selected,
                    fontWeight: 700,
                  }}>
                    CLASS
                  </TableCell>
                  {columns.map((c) => (
                    <TableCell key={`${c.day}|${c.session}`} sx={{
                      minWidth: 120,
                      bgcolor: theme.palette.action.selected,
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                        Day {c.day}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: 10, opacity: 0.8, display: 'block' }}>
                        {c.label}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: 9.5, opacity: 0.7 }}>
                        {c.start}–{c.end}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {classes.map((cls, row) => (
                  <TableRow key={cls.id}>
                    <TableCell sx={{
                      position: 'sticky', left: 0, zIndex: 1, fontWeight: 700,
                      bgcolor: row % 2 ? theme.palette.action.hover : theme.palette.background.paper,
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>{cls.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 9.5 }}>
                        {cls.placed} of {cls.papers} papers
                      </Typography>
                    </TableCell>
                    {columns.map((c) => {
                      const p = cellOf.get(`${cls.id}|${c.day}|${c.session}`);
                      const colours = p ? subjectChipColours({
                        name: p.subjectName, code: p.subjectCode, dark,
                      }) : null;
                      return (
                        <TableCell key={`${c.day}|${c.session}`} sx={{
                          bgcolor: row % 2 ? theme.palette.action.hover : undefined,
                          p: 0.5,
                        }}>
                          {p ? (
                            <Box sx={{
                              borderLeft: `3px solid ${colours.rule}`,
                              bgcolor: colours.background,
                              color: colours.ink,
                              borderRadius: '3px', px: 0.75, py: 0.5, lineHeight: 1.2,
                            }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: 11 }}>
                                {p.subjectName}
                              </Typography>
                            </Box>
                          ) : null}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Divider />

          <Typography variant="subtitle2">Papers</Typography>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Paper</TableCell>
                  <TableCell align="right">Classes sitting it</TableCell>
                  <TableCell>Sitting</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plan.papers.map((p) => (
                  <TableRow key={p.subjectId} hover>
                    <TableCell><Typography variant="body2">{p.subjectName}</Typography></TableCell>
                    <TableCell align="right">{p.classes}</TableCell>
                    <TableCell>
                      {p.sitting ? (
                        <Typography variant="caption" color="text.secondary">
                          Day {p.sitting.day} · {p.sitting.label} · {p.sitting.start}–{p.sitting.end}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="warning.main">no sitting left</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Stack>
  );
}
